import {assert} from 'Asserts';
import setIntersection from 'util/setIntersection';

import {AdjacencyMap, NodesToAncestors} from './GraphUtilConstants';

/**
 * Computes the lowest common ancestors (LCA) of the provided nodes.
 *
 * Reference algorithm: https://www.baeldung.com/cs/lowest-common-ancestor-acyclic-graph#approach-of-finding-lca-in-dag
 *
 * @param nodeIds The IDs of the nodes
 * @param nodesToAncestors A mapping of node IDs to their ancestors
 * @param adjacencyMapLoopsRemoved A mapping of node IDs to their neighbors, without "back" edges that form a loop
 * @returns The LCA of the provided nodes.
 */
export default function computeLowestCommonAncestors(
  nodeIds: Array<string>,
  nodesToAncestors: NodesToAncestors,
  adjacencyMapLoopsRemoved: AdjacencyMap
): Array<string> {
  assert(nodeIds.length > 0, 'Expected at least one node to be provided');
  if (nodeIds.length === 1) {
    return nodeIds;
  }
  let commonAncestors = nodesToAncestors[nodeIds[0]];
  for (const nodeId of nodeIds.slice(1)) {
    const ancestorIds = nodesToAncestors[nodeId];
    commonAncestors = setIntersection(commonAncestors, ancestorIds);
  }
  const lowestCommonAncestors = [];
  for (const commonAncestor of commonAncestors) {
    const nextNodes = adjacencyMapLoopsRemoved[commonAncestor];
    if (nextNodes == null || setIntersection(new Set([...nextNodes]), commonAncestors).size === 0) {
      lowestCommonAncestors.push(commonAncestor);
    }
  }
  return lowestCommonAncestors;
}
